import { fmt, iso } from '../utils/formatters';
import type { Token, Prices, FIFOResults, Position } from '../types';

/**
 * Tax Export Service - Export fiscal CSV avec calculs gains/pertes
 */

export interface TaxTransaction {
  date: string;
  type: 'Achat' | 'Vente' | 'Mining' | 'Staking' | 'Swap';
  token: string;
  quantity: number;
  priceUSD: number;
  priceEUR: number;
  totalCostEUR: number;
  realizedGainEUR?: number; // Pour ventes
  holdingPeriodDays?: number;
  gainType?: 'Court-terme' | 'Long-terme'; // < 1 an vs > 1 an
  notes?: string;

  // ✨ Swap Tracking (pour traçabilité et future conformité MICA)
  isSwap?: boolean;
  swapChainId?: string;
  originalCostBasis?: number;

  // 💎 Mining (pour traçabilité et future conformité MICA)
  miningPool?: string;
  miningHashrate?: number;

  // 🔒 Staking (pour traçabilité et future conformité MICA)
  stakingPlatform?: string;
  stakingAPY?: number;
  stakingDuration?: number;
  stakingCooldown?: number;
  stakingIsLocked?: boolean;
}

export interface TaxSummary {
  country: 'FR' | 'US' | 'UK' | 'DE';
  year: number;
  totalGains: number;
  totalLosses: number;
  netGain: number;
  shortTermGains: number; // < 1 an
  longTermGains: number;  // > 1 an
  taxEstimate: number; // Estimation basique
}

export interface TaxRules {
  shortTermRate: number;
  longTermRate: number;
  threshold: number;
  currency: string;
}

// Tax rules par pays
export const TAX_RULES: Record<'FR' | 'US' | 'UK' | 'DE', TaxRules> = {
  FR: {
    shortTermRate: 0.30, // PFU 30% (flat tax)
    longTermRate: 0.30,  // Pas d'avantage en France
    threshold: 305, // Seuil d'exonération 305€
    currency: 'EUR',
  },
  US: {
    shortTermRate: 0.37, // Taxé comme revenu ordinaire (max)
    longTermRate: 0.20,  // Capital gains > 1 an
    threshold: 0,
    currency: 'USD',
  },
  UK: {
    shortTermRate: 0.20,
    longTermRate: 0.20,
    threshold: 6000, // Capital Gains Tax allowance (GBP)
    currency: 'GBP',
  },
  DE: {
    shortTermRate: 0.26, // Kapitalertragsteuer
    longTermRate: 0,     // Exonération après 1 an!
    threshold: 1000, // Sparerpauschbetrag (EUR)
    currency: 'EUR',
  }
};

// Country display names
export const COUNTRY_NAMES: Record<'FR' | 'US' | 'UK' | 'DE', string> = {
  FR: '🇫🇷 France',
  US: '🇺🇸 États-Unis',
  UK: '🇬🇧 Royaume-Uni',
  DE: '🇩🇪 Allemagne',
};

/**
 * Calculate days between two dates
 */
function daysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate realized gain/loss for a sell position using FIFO
 */
function calculateRealizedGain(
  token: Token,
  sellPosition: Position,
  tokens: Token[]
): { gain: number; costBasis: number; holdingPeriodDays: number } {
  // Get all buy positions for this token before the sell date
  const buyPositions = token.positions
    .filter(p => p.type === 'buy' && p.date <= sellPosition.date)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  let remainingQty = sellPosition.quantity;
  let totalCostBasis = 0;
  let weightedHoldingDays = 0;

  // FIFO: Match sells with earliest buys
  for (const buy of buyPositions) {
    if (remainingQty <= 0) break;

    const qtyToMatch = Math.min(remainingQty, buy.quantity);
    const costBasis = qtyToMatch * buy.price;
    totalCostBasis += costBasis;

    // Calculate holding period for this lot
    const holdingDays = daysBetween(buy.date, sellPosition.date);
    weightedHoldingDays += holdingDays * qtyToMatch;

    remainingQty -= qtyToMatch;
  }

  const saleProceeds = sellPosition.quantity * sellPosition.price;
  const realizedGain = saleProceeds - totalCostBasis;
  const avgHoldingDays = sellPosition.quantity > 0
    ? Math.round(weightedHoldingDays / sellPosition.quantity)
    : 0;

  return {
    gain: realizedGain,
    costBasis: totalCostBasis,
    holdingPeriodDays: avgHoldingDays,
  };
}

/**
 * Export tax report CSV
 */
export function exportTaxReport(
  tokens: Token[],
  prices: Prices,
  fifoResults: FIFOResults,
  country: 'FR' | 'US' | 'UK' | 'DE',
  year: number
): void {
  const rules = TAX_RULES[country];
  const transactions: TaxTransaction[] = [];

  // Extract all transactions for the specified year
  tokens.forEach(token => {
    if (!token.positions || token.positions.length === 0) return;

    token.positions.forEach(pos => {
      const posDate = new Date(pos.date);
      if (posDate.getFullYear() !== year) return;

      const priceUSD = pos.price;
      const priceEUR = pos.price; // Simplified: In production, use actual EUR conversion

      // Determine transaction type
      let transactionType: 'Achat' | 'Vente' | 'Mining' | 'Staking' | 'Swap';
      if (pos.type === 'buy') {
        transactionType = 'Achat';
      } else if (pos.type === 'sell') {
        transactionType = 'Vente';
      } else if (pos.type === 'mining') {
        transactionType = 'Mining';
      } else if (pos.type === 'staking') {
        transactionType = 'Staking';
      } else {
        transactionType = 'Achat'; // Fallback
      }

      // Check if this is a swap (buy/sell with is_swap metadata)
      if ((pos as any).is_swap) {
        transactionType = 'Swap';
      }

      const transaction: TaxTransaction = {
        date: pos.date,
        type: transactionType,
        token: token.displayName || token.id,
        quantity: pos.quantity,
        priceUSD,
        priceEUR,
        totalCostEUR: pos.quantity * priceEUR,
        notes: pos.note,

        // ✨ Swap Tracking (pour traçabilité et future conformité MICA)
        isSwap: (pos as any).is_swap || false,
        swapChainId: (pos as any).swap_chain_id,
        originalCostBasis: (pos as any).original_cost_basis,

        // 💎 Mining (pour traçabilité et future conformité MICA)
        miningPool: (pos as any).mining_pool,
        miningHashrate: (pos as any).mining_hashrate,

        // 🔒 Staking (pour traçabilité et future conformité MICA)
        stakingPlatform: (pos as any).staking_platform,
        stakingAPY: (pos as any).staking_apy,
        stakingDuration: (pos as any).staking_duration,
        stakingCooldown: (pos as any).staking_cooldown,
        stakingIsLocked: (pos as any).staking_is_locked,
      };

      // Calculate gain/loss for sell positions
      if (pos.type === 'sell') {
        const { gain, costBasis, holdingPeriodDays } = calculateRealizedGain(
          token,
          pos,
          tokens
        );

        transaction.realizedGainEUR = gain;
        transaction.holdingPeriodDays = holdingPeriodDays;
        transaction.gainType = holdingPeriodDays >= 365 ? 'Long-terme' : 'Court-terme';
      }

      transactions.push(transaction);
    });
  });

  // Sort by date
  transactions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Calculate summary
  const summary = calculateTaxSummary(transactions, country, year);

  // Generate CSV content
  const csv = generateTaxCSV(transactions, summary, country, year);

  // Download CSV
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `rapport_fiscal_${year}_${country}_${iso(new Date())}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Calculate tax summary
 */
function calculateTaxSummary(
  transactions: TaxTransaction[],
  country: 'FR' | 'US' | 'UK' | 'DE',
  year: number
): TaxSummary {
  const rules = TAX_RULES[country];

  let totalGains = 0;
  let totalLosses = 0;
  let shortTermGains = 0;
  let longTermGains = 0;

  transactions.forEach(tx => {
    if (tx.type === 'Vente' && tx.realizedGainEUR !== undefined) {
      if (tx.realizedGainEUR > 0) {
        totalGains += tx.realizedGainEUR;
        if (tx.gainType === 'Court-terme') {
          shortTermGains += tx.realizedGainEUR;
        } else {
          longTermGains += tx.realizedGainEUR;
        }
      } else {
        totalLosses += Math.abs(tx.realizedGainEUR);
      }
    }
  });

  const netGain = totalGains - totalLosses;

  // Calculate tax estimate
  let taxEstimate = 0;
  if (netGain > rules.threshold) {
    const taxableGain = netGain - rules.threshold;
    taxEstimate =
      (shortTermGains * rules.shortTermRate) +
      (longTermGains * rules.longTermRate);
  }

  return {
    country,
    year,
    totalGains,
    totalLosses,
    netGain,
    shortTermGains,
    longTermGains,
    taxEstimate,
  };
}

/**
 * Generate CSV content
 */
function generateTaxCSV(
  transactions: TaxTransaction[],
  summary: TaxSummary,
  country: 'FR' | 'US' | 'UK' | 'DE',
  year: number
): string {
  const rules = TAX_RULES[country];
  const lines: string[] = [];

  // Header
  lines.push(`# RAPPORT FISCAL ${year} - Edouard.ai`);
  lines.push(`# Pays: ${COUNTRY_NAMES[country]}`);
  lines.push(`# Méthode: FIFO (First In, First Out) avec Cost Basis Chain Tracking`);
  lines.push(`# Généré le: ${new Date().toLocaleDateString('fr-FR')}`);
  lines.push('');
  lines.push('# ⚠️ IMPORTANT: Les types Mining, Staking et Swap sont inclus pour traçabilité');
  lines.push('# mais ne sont PAS encore calculés dans les totaux fiscaux.');
  lines.push('# Ces données sont préparées pour la future conformité MiCA (Markets in Crypto-Assets).');
  lines.push('');

  // Column headers
  lines.push('Date,Type,Token,Quantité,Prix Achat (EUR),Prix Vente (EUR),Coût Total (EUR),Plus-Value (EUR),Durée Détention (jours),Type Gain,Is Swap,Swap Chain ID,Original Cost Basis,Mining Pool,Mining Hashrate,Staking Platform,Staking APY,Staking Duration,Staking Cooldown,Staking Locked,Notes');

  // Transaction rows
  transactions.forEach(tx => {
    const row = [
      tx.date,
      tx.type,
      tx.token,
      fmt(tx.quantity, 8),
      tx.type === 'Achat' ? fmt(tx.priceEUR, 2) : '',
      tx.type === 'Vente' ? fmt(tx.priceEUR, 2) : '',
      fmt(tx.totalCostEUR, 2),
      tx.realizedGainEUR !== undefined ? fmt(tx.realizedGainEUR, 2) : '',
      tx.holdingPeriodDays !== undefined ? tx.holdingPeriodDays.toString() : '',
      tx.gainType || '',
      // ✨ Swap Tracking
      tx.isSwap ? 'OUI' : 'NON',
      tx.swapChainId || '',
      tx.originalCostBasis !== undefined ? fmt(tx.originalCostBasis, 2) : '',
      // 💎 Mining
      tx.miningPool || '',
      tx.miningHashrate !== undefined ? fmt(tx.miningHashrate, 2) : '',
      // 🔒 Staking
      tx.stakingPlatform || '',
      tx.stakingAPY !== undefined ? fmt(tx.stakingAPY, 2) + '%' : '',
      tx.stakingDuration !== undefined ? tx.stakingDuration.toString() + ' jours' : '',
      tx.stakingCooldown !== undefined ? tx.stakingCooldown.toString() + ' jours' : '',
      tx.stakingIsLocked !== undefined ? (tx.stakingIsLocked ? 'Locked' : 'Flexible') : '',
      // Notes
      tx.notes ? `"${tx.notes.replace(/"/g, '""')}"` : '',
    ];
    lines.push(row.join(','));
  });

  // Summary section
  lines.push('');
  lines.push('# RÉSUMÉ FISCAL (Achats/Ventes uniquement)');
  lines.push(`Total Plus-Values:,${fmt(summary.totalGains, 2)} ${rules.currency}`);
  lines.push(`Total Moins-Values:,${fmt(summary.totalLosses, 2)} ${rules.currency}`);
  lines.push(`Plus-Value Nette:,${fmt(summary.netGain, 2)} ${rules.currency}`);
  lines.push(`Gains Court-terme (<1 an):,${fmt(summary.shortTermGains, 2)} ${rules.currency}`);
  lines.push(`Gains Long-terme (>1 an):,${fmt(summary.longTermGains, 2)} ${rules.currency}`);
  lines.push(`Seuil d'exonération:,${fmt(rules.threshold, 2)} ${rules.currency}`);
  lines.push(`Taux Court-terme:,${(rules.shortTermRate * 100).toFixed(0)}%`);
  lines.push(`Taux Long-terme:,${(rules.longTermRate * 100).toFixed(0)}%`);
  lines.push(`Estimation Impôt:,${fmt(summary.taxEstimate, 2)} ${rules.currency}`);

  // Count special transaction types
  const swapCount = transactions.filter(tx => tx.type === 'Swap' || tx.isSwap).length;
  const miningCount = transactions.filter(tx => tx.type === 'Mining').length;
  const stakingCount = transactions.filter(tx => tx.type === 'Staking').length;

  lines.push('');
  lines.push('# TRAÇABILITÉ (données incluses pour future conformité MiCA)');
  lines.push(`Transactions Swap:,${swapCount} (non calculées fiscalement)`);
  lines.push(`Transactions Mining:,${miningCount} (non calculées fiscalement)`);
  lines.push(`Transactions Staking:,${stakingCount} (non calculées fiscalement)`);
  lines.push('');
  lines.push('# Note: Ces types de transactions sont tracés pour traçabilité complète');
  lines.push('# et seront calculés fiscalement dans une future mise à jour conforme MiCA.');

  // Disclaimer
  lines.push('');
  lines.push('# DISCLAIMER');
  lines.push('# Ce rapport est fourni à titre informatif uniquement.');
  lines.push('# Consultez un expert-comptable pour votre déclaration fiscale officielle.');
  lines.push('# Les taux d\'imposition sont des estimations et peuvent varier selon votre situation.');

  return lines.join('\n');
}
